/**
 * Emailing paperwork to the customer.
 *
 * Sends a document already on file rather than rebuilding one. What the
 * customer receives has to be the copy that was signed and archived — a
 * freshly generated packet would be a different file, with a different hash,
 * and the whole point of hashing it was to be able to say later that this is
 * the document.
 */

import { useEffect, useState } from "react";
import { Button, ErrorNote, Panel } from "../../components/ui";
import { EmailField, TextField } from "../../components/fields";
import { emailDocument, workerConfigured } from "../../lib/stripe";
import {
  listDealDocuments, fetchArchivedDocument, labelForDocType, formatSize,
  type ArchivedDoc,
} from "../../lib/documents";
import { supabase } from "../../lib/supabase";
import { usDate } from "../../forms/mappings";
import { describeSaveError, isOffline } from "../../lib/saving";

interface Send {
  id: string; to_email: string; subject: string; sent_at: string; error: string | null;
}

const toBase64 = (bytes: Uint8Array): string => {
  // Chunked: a megabyte packet spread over one String.fromCharCode call
  // overflows the argument list and throws.
  let out = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    out += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(out);
};

export default function EmailPacket({
  dealId, dealNo, buyerName, buyerEmail, vehicle, vin, dealerName, filedCount,
}: {
  dealId: string;
  dealNo: string;
  buyerName: string;
  buyerEmail: string | null;
  vehicle: string;
  vin: string;
  dealerName: string;
  /** Bumped by the deal screen whenever it refiles something. Without it this
      panel read the archive once, on mount, and a packet filed afterwards —
      which is every packet, since signing happens after this renders — left
      it still saying nothing had been filed. */
  filedCount?: number;
}) {
  const [docs, setDocs] = useState<ArchivedDoc[]>([]);
  const [sends, setSends] = useState<Send[]>([]);
  const [pick, setPick] = useState<string>("");
  const [to, setTo] = useState(buyerEmail ?? "");
  const [subject, setSubject] = useState(`Your paperwork — ${vehicle}`);
  /* The vehicle and VIN are shown in the email's own header block, so the
     message does not repeat them. */
  const [message, setMessage] = useState(
    `Hi ${buyerName.split(" ")[0] ?? buyerName},\n\n`
    + `Your paperwork is attached — a complete copy of everything from today.\n\n`
    + `Keep it somewhere safe. You'll want it for your records, and the tag and `
    + `title work references it.\n\n`
    + `If anything looks wrong, just reply to this email and we'll sort it out.\n\n`
    + `Thanks,\n${dealerName}`,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /* What the last send actually did. Kept as a record rather than a string,
     because "it worked" needs to say to whom, which document, and when — a
     one-line note above a form that still looks ready to send is the kind of
     confirmation people miss and then send twice. */
  const [sent, setSent] = useState<
    { to: string; docLabel: string; at: Date; warning?: string } | null>(null);

  const load = () => {
    listDealDocuments(dealId)
      .then((d) => {
        setDocs(d);
        // Default to the signed packet when there is one; it is what the
        // customer actually wants a copy of.
        const best = d.find((x) => x.doc_type === "signed_packet") ?? d[0];
        if (best) setPick((p) => p || best.id);
      })
      .catch((e: Error) => setErr(e.message));

    supabase.from("document_sends")
      .select("id,to_email,subject,sent_at,error")
      .eq("deal_id", dealId).order("sent_at", { ascending: false }).limit(10)
      .then(({ data }) => setSends((data ?? []) as Send[]));
  };
  useEffect(load, [dealId, filedCount]);

  const send = async () => {
    const doc = docs.find((d) => d.id === pick);
    if (!doc) { setErr("Choose which document to send."); return; }
    if (!to.trim()) { setErr("Where should it go?"); return; }

    setBusy(true); setErr(null); setSent(null);
    try {
      if (isOffline()) throw new Error("offline");
      const bytes = await fetchArchivedDocument(doc.storage_path);
      const result = await emailDocument({
        to: to.trim(),
        subject,
        message,
        filename: `${dealNo}-${doc.doc_type}.pdf`,
        pdfBase64: toBase64(bytes),
        dealId,
        context: { dealNo, vehicle, vin },
      });
      setSent({
        to: to.trim(),
        docLabel: labelForDocType(doc.doc_type),
        at: new Date(),
        warning: result.warning,
      });
      load();
    } catch (e) {
      // Plain words for a dropped connection; the message stays filled in.
      setErr(`Not sent. ${describeSaveError(e)} Press Send it to try again.`);
    } finally {
      setBusy(false);
    }
  };

  if (!workerConfigured()) {
    return (
      <Panel title="Email the paperwork" rail="record">
        <p className="hint">
          Email needs the worker configured. Set VITE_PAYMENTS_WORKER and deploy.
        </p>
      </Panel>
    );
  }

  return (
    <Panel title="Email the paperwork" rail="record"
           figure={sends.length ? `${sends.length} sent` : undefined}>
      {err && <ErrorNote>{err}</ErrorNote>}

      {sent ? (
        <div className="sentcard">
          <div className="sentcard-top">
            <span className="tick-big" aria-hidden="true">✓</span>
            <div>
              <strong>Sent to {sent.to}</strong>
              <span className="hint">
                {sent.docLabel} · {sent.at.toLocaleTimeString([], {
                  hour: "numeric", minute: "2-digit" })}
              </span>
            </div>
          </div>
          {sent.warning && <p className="taxnote">{sent.warning}</p>}
          <p className="taxnote">
            If it does not arrive within a few minutes, check the spam folder
            before sending again — a second copy of the same paperwork confuses
            more than it helps.
          </p>
          <div className="actions">
            <Button variant="ghost" onClick={() => setSent(null)}>Send another</Button>
          </div>
        </div>
      ) : docs.length === 0 ? (
        <p className="hint">
          Nothing filed against this deal yet. Print or sign the packet first —
          only a document that has been filed can be emailed, so what the
          customer receives is the copy on record.
        </p>
      ) : (
        <>
          <div className="field">
            <label htmlFor="doc-pick">Which document</label>
            <select id="doc-pick" value={pick} onChange={(e) => setPick(e.target.value)}>
              {docs.map((d) => (
                <option key={d.id} value={d.id}>
                  {labelForDocType(d.doc_type)} — {usDate(d.generated_at.slice(0, 10))}
                  {" "}({formatSize(d.byte_size)})
                </option>
              ))}
            </select>
          </div>

          <div className="row2">
            <EmailField label="To" value={to} onChange={setTo} required />
            <TextField label="Subject" value={subject} onChange={setSubject} />
          </div>

          <div className="field">
            <label htmlFor="mail-body">Message</label>
            <textarea id="mail-body" rows={9} value={message}
                      onChange={(e) => setMessage(e.target.value)} />
          </div>

          <div className="actions">
            <Button disabled={busy || !pick} onClick={send}>
              {busy ? "Sending…" : "Send it"}
            </Button>
          </div>
        </>
      )}

      {sends.length > 0 && (
        <details className="donelist">
          <summary>Previously sent</summary>
          {sends.map((s) => (
            <div className="li" key={s.id}>
              <span>
                {s.to_email}
                {s.error && <span className="flag late">failed</span>}
              </span>
              <span className="hint">{new Date(s.sent_at).toLocaleString()}</span>
            </div>
          ))}
        </details>
      )}
    </Panel>
  );
}
